import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { isMobile } from 'react-device-detect';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';
import { handleLaunchExternalLink } from 'Shared/utils/external-link';

// Hooks
import { useCopyToClipboard } from 'Shared/utils/hooks/use-copy-to-clipboard';
import { useApp } from 'Shared/utils/hooks/use-app';
import { useTranslation } from 'Shared/contexts/TranslationsContext';

// Actions
import { starWorkspace, unstarWorkspace } from 'Shared/actions/workspaces';

// Components
import WpContextMenu from 'Wpds/components/context-menu/WpContextMenu';
import { WpMenu, WpMenuItem } from 'Wpds/components/menu';
import WpIcon from 'Wpds/components/icon/WpIcon';

type Props = {
  children: React.ReactNode;
  workspaceId: string;
  starred: boolean;
};

const ContextMenuWrapper = ({ children, workspaceId, starred }: Props) => {
  const dispatch = useDispatch();
  const { isZoom } = useApp();
  const { tr } = useTranslation();

  const workspaceUrl = `${window.location.origin}${PageUrlsUtil.makeWorkspaceUrl(workspaceId)}`;

  const { onCopyTextToClipboard: handleCopyLinkClick } = useCopyToClipboard({
    textToCopy: workspaceUrl,
  });

  const handleOpenInNewTabClick = () => {
    handleLaunchExternalLink({ href: workspaceUrl });
  };

  const handleStarClick = useCallback(() => {
    if (starred) {
      dispatch(unstarWorkspace({ workspaceId }));
    } else {
      dispatch(starWorkspace({ workspaceId }));
    }
  }, [starred, dispatch, workspaceId]);

  if (isZoom || isMobile) return <>{children}</>;

  return (
    <WpContextMenu
      openClassName="active"
      menu={
        <WpMenu>
          <WpMenuItem onClick={handleOpenInNewTabClick}>
            <WpIcon name="external-link" size="sm" />
            {tr('perform.navigation.team_group_section.open_in_new_tab')}
          </WpMenuItem>
          <WpMenuItem onClick={handleCopyLinkClick}>
            <WpIcon name="link" size="sm" />
            {tr('perform.common.copy_link')}
          </WpMenuItem>
          <WpMenuItem onClick={handleStarClick}>
            <WpIcon name="star" size="sm" />
            {starred
              ? tr('perform.navigation.team_group_section.remove_from_starred')
              : tr('perform.navigation.team_group_section.add_to_starred')}
          </WpMenuItem>
        </WpMenu>
      }
    >
      {children}
    </WpContextMenu>
  );
};

export default ContextMenuWrapper;
